/**
 * CREATIVE DETONATOR - Storyboard State Manager
 * Centralized state management for video storyboards
 *
 * Features:
 * - Per-idea storyboard tracking (no state bleeding)
 * - SessionStorage fallback for generator ideas
 * - Database persistence for saved ideas
 * - Scene generation status tracking
 */

import { API_CONFIG, GeneratorState } from '../core/state.js';
import { isValidUUID, UUID_REGEX } from '../../../js/core/uuid-utils.js';
import { TIMEOUTS, RETRY } from '../../../js/core/constants.js';
import { apiRequest } from '../../../js/api/client.js';

// ============================================================================
// STORYBOARD STATE
// ============================================================================

const StoryboardState = {
  // Storyboards by idea ID
  byIdeaId: {},

  // Currently active idea ID in modal
  activeIdeaId: null,

  // Current active scene index (0-based)
  activeSceneIndex: 0,

  // Dirty flag for unsaved changes
  isDirty: false,

  // Generation status per idea
  generationStatus: {}, // ideaId -> 'idle' | 'generating' | 'complete' | 'error'

  // Frame generation status
  frameGenerationStatus: {}, // "ideaId_sceneSeq_frameType" -> 'idle' | 'generating' | 'complete' | 'error'

  // Panel pools from split storyboard grids (ideaId -> panel array)
  panelPools: {},

  // Split settings per idea (rows, cols)
  splitSettings: {},

  // Source image URLs for change detection
  sourceImages: {}
};

// Session storage key for generator ideas
const STORAGE_KEY = 'creative_detonator_storyboards';

// ============================================================================
// STATE ACCESSORS
// ============================================================================

/**
 * Get storyboard for a specific idea
 * DATABASE-FIRST PATTERN (Q1):
 * - For database ideas: data is loaded from DB via loadFromIdea() when idea opens
 * - Memory serves as cache, database is source of truth
 * - SessionStorage is only used for temporary generator ideas (not yet saved)
 *
 * @param {string} ideaId - The idea ID
 * @returns {Object|null} Storyboard data or null
 */
export function getStoryboard(ideaId) {
  // Check memory cache first (populated from DB via loadFromIdea for saved ideas)
  if (StoryboardState.byIdeaId[ideaId]) {
    return StoryboardState.byIdeaId[ideaId];
  }

  // SessionStorage fallback - only for temporary generator ideas
  // Database ideas should already be in memory via loadFromIdea()
  if (!isDatabaseIdea(ideaId)) {
    const stored = loadFromSession();
    if (stored.byIdeaId[ideaId]) {
      // Normalize on load from session (fixes old data with prefixes)
      const normalized = normalizeStoryboard(stored.byIdeaId[ideaId]);
      StoryboardState.byIdeaId[ideaId] = normalized;
      return normalized;
    }
  }

  return null;
}

/**
 * Set storyboard for a specific idea
 * DATABASE-FIRST PATTERN (Q1): For saved ideas, database is source of truth
 * - Database ideas: Save to DB first, memory is cache
 * - Generator ideas: Use sessionStorage (temporary until saved)
 *
 * @param {string} ideaId - The idea ID
 * @param {Object} storyboard - Storyboard data
 */
export function setStoryboard(ideaId, storyboard) {
  // Add metadata
  storyboard.version = storyboard.version || 1;
  storyboard.generatedAt = storyboard.generatedAt || new Date().toISOString();
  storyboard.ideaId = ideaId;

  // Store in StoryboardState memory (cache)
  StoryboardState.byIdeaId[ideaId] = storyboard;

  // DATABASE-FIRST: For saved ideas, prioritize database save
  if (isDatabaseIdea(ideaId)) {
    // Mark as dirty until confirmed saved
    StoryboardState.isDirty = true;
    // Trigger database save (async with retry)
    saveToDatabase(ideaId);
  } else {
    // For temporary generator ideas, use sessionStorage
    saveToSession();
  }

  // SYNC TO GENERATOR STATE
  // This ensures when user saves the IDEA, storyboard goes with it
  if (GeneratorState.generatedIdeas) {
    for (const group of GeneratorState.generatedIdeas) {
      const idea = group.ideas?.find(i => i.id === ideaId);
      if (idea) {
        if (!idea.meta) idea.meta = {};
        idea.meta.storyboard = storyboard;
        console.log('[Storyboard] Synced to GeneratorState for idea:', ideaId);
        break;
      }
    }
  }

  // Update status
  StoryboardState.generationStatus[ideaId] = 'complete';
}

/**
 * Get active storyboard (for current modal)
 * @returns {Object|null}
 */
export function getActiveStoryboard() {
  if (!StoryboardState.activeIdeaId) return null;
  return getStoryboard(StoryboardState.activeIdeaId);
}

/**
 * Get active scene (for current modal)
 * @returns {Object|null}
 */
export function getActiveScene() {
  const storyboard = getActiveStoryboard();
  if (!storyboard?.scenes) return null;
  return storyboard.scenes[StoryboardState.activeSceneIndex] || null;
}

/**
 * Set active idea ID
 * Only resets scene index if switching to a different idea
 * @param {string} ideaId
 */
export function setActiveIdeaId(ideaId) {
  // Only reset scene index if switching to a different idea
  if (StoryboardState.activeIdeaId !== ideaId) {
    StoryboardState.activeIdeaId = ideaId;
    StoryboardState.activeSceneIndex = 0;
  }
}

/**
 * Set active scene index
 * @param {number} index
 */
export function setActiveSceneIndex(index) {
  const storyboard = getActiveStoryboard();
  if (storyboard?.scenes && index >= 0 && index < storyboard.scenes.length) {
    StoryboardState.activeSceneIndex = index;
  }
}

/**
 * Get active scene index
 * @returns {number}
 */
export function getActiveSceneIndex() {
  return StoryboardState.activeSceneIndex;
}

/**
 * Get active idea ID directly from state
 * Use this instead of extracting from storyboard when you need the idea ID
 * regardless of whether a storyboard exists (e.g., panels can exist without scenes)
 * @returns {string|null}
 */
export function getActiveIdeaId() {
  return StoryboardState.activeIdeaId;
}

// ============================================================================
// GENERATION STATUS
// ============================================================================

/**
 * Set generation status for an idea
 * @param {string} ideaId
 * @param {'idle'|'generating'|'complete'|'error'} status
 */
export function setGenerationStatus(ideaId, status) {
  StoryboardState.generationStatus[ideaId] = status;
}

/**
 * Get generation status for an idea
 * @param {string} ideaId
 * @returns {'idle'|'generating'|'complete'|'error'}
 */
export function getGenerationStatus(ideaId) {
  return StoryboardState.generationStatus[ideaId] || 'idle';
}

/**
 * Check if any idea is currently generating
 * @returns {boolean}
 */
export function isAnyGenerating() {
  return Object.values(StoryboardState.generationStatus).includes('generating');
}

// ============================================================================
// FRAME GENERATION STATUS
// ============================================================================

/**
 * Get frame generation status key
 */
function getFrameKey(ideaId, sceneSeq, frameType) {
  return `${ideaId}_${sceneSeq}_${frameType}`;
}

/**
 * Set frame generation status
 * @param {number} sceneSeq
 * @param {'start'|'end'} frameType
 * @param {'idle'|'generating'|'complete'|'error'} status
 */
export function setFrameStatus(sceneSeq, frameType, status) {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return;
  const key = getFrameKey(ideaId, sceneSeq, frameType);
  StoryboardState.frameGenerationStatus[key] = status;
}

/**
 * Get frame generation status
 * @param {number} sceneSeq
 * @param {'start'|'end'} frameType
 * @returns {'idle'|'generating'|'complete'|'error'}
 */
export function getFrameStatus(sceneSeq, frameType) {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return 'idle';
  const key = getFrameKey(ideaId, sceneSeq, frameType);
  return StoryboardState.frameGenerationStatus[key] || 'idle';
}

// ============================================================================
// PANEL POOL MANAGEMENT
// ============================================================================

/**
 * Set panel pool for an idea (from split storyboard grid)
 * WARNING: This REPLACES the entire pool. For preserving user uploads on RE-SPLIT, use addPanelsToPool().
 * @param {string} ideaId
 * @param {Array} panels - Array of panel objects with url/dataUrl, index, row, col
 */
export function setPanelPool(ideaId, panels) {
  StoryboardState.panelPools[ideaId] = panels;
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Set panel pool for', ideaId, ':', panels.length, 'panels');
}

/**
 * Add panels to existing pool (accumulate mode - keeps all existing panels)
 * Use this for RE-SPLIT operations to build up a library of panel options.
 * @param {string} ideaId
 * @param {Array} newPanels - New panels to add
 * @param {boolean} replace - If true, replaces entire pool (used for clear all)
 * @returns {Array} The merged panel array
 */
export function addPanelsToPool(ideaId, newPanels, replace = false) {
  const currentPool = StoryboardState.panelPools[ideaId] || [];

  let mergedPanels;
  if (replace) {
    // Full replace (used for clear all)
    mergedPanels = newPanels;
  } else {
    // ACCUMULATE MODE: Keep ALL existing panels, append new ones
    // This gives users more options to choose from
    const taggedNewPanels = newPanels.map(p => ({ ...p, source: p.source || 'split' }));
    mergedPanels = [...currentPool, ...taggedNewPanels];
  }

  // Re-index
  mergedPanels.forEach((p, i) => p.index = i + 1);

  StoryboardState.panelPools[ideaId] = mergedPanels;
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);  // Fire and forget
  }

  const uploadCount = mergedPanels.filter(p => p.source === 'upload').length;
  const splitCount = mergedPanels.filter(p => p.source === 'split').length;
  console.log(`[Storyboard] Panel pool: ${mergedPanels.length} total (${splitCount} splits, ${uploadCount} uploads)`);
  return mergedPanels;
}

/**
 * Clear all split panels from pool (keeps user uploads AND edited panels)
 * @param {string} ideaId
 * @param {boolean} deleteFromR2 - If true, also deletes split images from R2
 */
export async function clearSplitPanels(ideaId, deleteFromR2 = true) {
  const pool = StoryboardState.panelPools[ideaId] || [];
  const splitPanels = pool.filter(p => p.source === 'split');
  // Keep uploads AND edited panels (anything that's NOT a split panel)
  const userPanels = pool.filter(p => p.source !== 'split');

  // Delete split panels from R2 if requested (authenticated)
  if (deleteFromR2) {
    for (const panel of splitPanels) {
      if (panel.path) {
        try {
          const response = await apiRequest('/api/storage/delete', {
            method: 'POST',
            body: JSON.stringify({ path: panel.path })
          });
          if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            console.warn('[Storyboard] R2 delete failed:', data.error || response.status);
          }
        } catch (e) {
          console.warn('[Storyboard] Failed to delete panel from R2:', e);
        }
      }
    }
  }

  // Keep user uploads AND edited panels
  userPanels.forEach((p, i) => p.index = i + 1);
  StoryboardState.panelPools[ideaId] = userPanels;
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  const uploadCount = userPanels.filter(p => p.source === 'upload').length;
  const editedCount = userPanels.filter(p => p.source === 'edited').length;
  console.log(`[Storyboard] Cleared ${splitPanels.length} split panels, kept ${uploadCount} uploads, ${editedCount} edited`);
  return userPanels;
}

/**
 * Get panel pool for an idea
 * @param {string} ideaId
 * @returns {Array} Array of panel objects or empty array
 */
export function getPanelPool(ideaId) {
  return StoryboardState.panelPools[ideaId] || [];
}

/**
 * Check if an idea has a panel pool
 * @param {string} ideaId
 * @returns {boolean}
 */
export function hasPanelPool(ideaId) {
  return (StoryboardState.panelPools[ideaId]?.length || 0) > 0;
}

/**
 * Clear panel pool for an idea
 * @param {string} ideaId
 */
export function clearPanelPool(ideaId) {
  delete StoryboardState.panelPools[ideaId];
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Cleared panel pool for', ideaId);
}

/**
 * Invalidate (clear) storyboard cache for an idea
 * Use this before loadFromIdea() when the source idea has been updated
 * (e.g., after refinement updates scenes)
 *
 * This solves the cache staleness problem where loadFromIdea() would
 * early-return when scenes already exist in memory, ignoring newer
 * data in the idea object.
 *
 * @param {string} ideaId - The idea ID
 */
export function invalidateStoryboard(ideaId) {
  if (!ideaId) return;
  const id = String(ideaId);

  if (StoryboardState.byIdeaId[id]) {
    console.log('[Storyboard] Invalidating cache for idea:', id);
    delete StoryboardState.byIdeaId[id];
  }
}

/**
 * Remove a single panel from the pool and optionally delete from R2
 * @param {string} ideaId
 * @param {number} panelIndex - 0-based index in the pool array
 * @param {boolean} deleteFromR2 - If true, also deletes the image from R2 storage
 */
export async function removePanelFromPool(ideaId, panelIndex, deleteFromR2 = true) {
  const pool = StoryboardState.panelPools[ideaId];
  if (!pool) return;

  const panel = pool[panelIndex];

  // Delete from R2 if path exists and flag is true (authenticated)
  if (deleteFromR2 && panel?.path) {
    try {
      const response = await apiRequest('/api/storage/delete', {
        method: 'POST',
        body: JSON.stringify({ path: panel.path })
      });
      const result = await response.json();
      if (result.success) {
        console.log('[Storyboard] Deleted panel from R2:', panel.path);
      } else {
        console.warn('[Storyboard] R2 delete failed:', result.error);
      }
    } catch (e) {
      console.warn('[Storyboard] Failed to delete from R2:', e);
      // Continue with local removal even if R2 delete fails
    }
  }

  pool.splice(panelIndex, 1);

  // Re-index remaining panels
  pool.forEach((p, i) => {
    p.index = i + 1;
  });

  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Removed panel', panelIndex + 1, 'from pool');
}

/**
 * Add a panel to the pool
 * @param {string} ideaId
 * @param {Object} panel - Panel object with dataUrl
 */
export function addPanelToPool(ideaId, panel) {
  if (!StoryboardState.panelPools[ideaId]) {
    StoryboardState.panelPools[ideaId] = [];
  }

  const pool = StoryboardState.panelPools[ideaId];
  panel.index = pool.length + 1;
  pool.push(panel);

  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Added panel to pool:', panel.index);
}

/**
 * Duplicate a specific panel in the pool
 * Creates a copy of the panel at panelIndex and inserts it immediately after
 * @param {string} ideaId
 * @param {number} panelIndex - 0-based index of the panel to duplicate
 * @returns {Object|null} The new panel or null on failure
 */
export function duplicatePanelInPool(ideaId, panelIndex) {
  const pool = StoryboardState.panelPools[ideaId];
  if (!pool || !pool[panelIndex]) {
    console.warn('[Storyboard] Cannot duplicate: panel not found at index', panelIndex);
    return null;
  }

  // Deep clone the panel object
  const sourcePanel = pool[panelIndex];
  const newPanel = JSON.parse(JSON.stringify(sourcePanel));

  // Mark as a duplicate (useful for debugging)
  newPanel.isDuplicate = true;
  newPanel.duplicatedFrom = panelIndex + 1;
  newPanel.duplicatedAt = new Date().toISOString();

  // Keep the same URL - no new storage cost
  // Note: dataUrl field is preserved from source

  // Insert immediately after the original
  pool.splice(panelIndex + 1, 0, newPanel);

  // Re-index all panels (1, 2, 3...)
  pool.forEach((p, i) => {
    p.index = i + 1;
  });

  // Save changes
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log(`[Storyboard] Duplicated panel ${panelIndex + 1} → new panel at ${panelIndex + 2}`);
  return newPanel;
}

/**
 * Update a panel in the pool with a new image URL
 * Used when editing a panel via contextual image editing
 * @param {string} ideaId
 * @param {number} panelIndex - 0-based index of the panel to update
 * @param {string} imageUrl - New image URL
 * @param {string} source - Source of the new image ('edited', 'generated', etc.)
 * @returns {Object|null} The updated panel or null on failure
 */
export function updatePanelInPool(ideaId, panelIndex, imageUrl, source = 'edited') {
  const pool = StoryboardState.panelPools[ideaId];
  if (!pool || !pool[panelIndex]) {
    console.warn('[Storyboard] Cannot update: panel not found at index', panelIndex);
    return null;
  }

  // Update the panel with new image URL
  pool[panelIndex] = {
    ...pool[panelIndex],
    dataUrl: imageUrl,
    url: imageUrl,
    editedAt: new Date().toISOString(),
    source: source,
    isEditing: false,
    // Keep original index
    index: pool[panelIndex].index
  };

  // Save changes
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log(`[Storyboard] Updated panel ${panelIndex + 1} with new image (source: ${source})`);
  return pool[panelIndex];
}

/**
 * Set panel editing status (for loading UI)
 * @param {string} ideaId
 * @param {number} panelIndex
 * @param {boolean} isEditing
 */
export function setPanelEditingStatus(ideaId, panelIndex, isEditing) {
  const pool = StoryboardState.panelPools[ideaId];
  if (!pool || !pool[panelIndex]) return;

  pool[panelIndex].isEditing = isEditing;
  // Don't save to session for transient editing status
}

/**
 * Get panel editing status
 * @param {string} ideaId
 * @param {number} panelIndex
 * @returns {boolean}
 */
export function getPanelEditingStatus(ideaId, panelIndex) {
  const pool = StoryboardState.panelPools[ideaId];
  return pool?.[panelIndex]?.isEditing || false;
}

// ============================================================================
// SPLIT SETTINGS
// ============================================================================

/**
 * Get split settings for an idea
 * @param {string} ideaId
 * @returns {Object} { rows: number, cols: number }
 */
export function getSplitSettings(ideaId) {
  return StoryboardState.splitSettings[ideaId] || { rows: 3, cols: 2 };
}

/**
 * Set split settings for an idea
 * Only triggers database save if values actually changed (prevents timeout on canvas open)
 * @param {string} ideaId
 * @param {number} rows
 * @param {number} cols
 */
export function setSplitSettings(ideaId, rows, cols) {
  const current = StoryboardState.splitSettings[ideaId];

  // Skip if values unchanged (prevents unnecessary DB saves on canvas open)
  if (current?.rows === rows && current?.cols === cols) {
    return;
  }

  StoryboardState.splitSettings[ideaId] = { rows, cols };
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }
}

// ============================================================================
// SOURCE IMAGE TRACKING (for change detection)
// ============================================================================

/**
 * Get tracked source image URL for an idea
 * @param {string} ideaId
 * @returns {string|null}
 */
export function getSourceImage(ideaId) {
  return StoryboardState.sourceImages[ideaId] || null;
}

/**
 * Set/track source image URL for an idea
 * Only triggers database save if URL actually changed
 * @param {string} ideaId
 * @param {string} imageUrl
 */
export function setSourceImage(ideaId, imageUrl) {
  const current = StoryboardState.sourceImages[ideaId];

  // Skip if URL unchanged (prevents unnecessary DB saves)
  if (current === imageUrl) {
    return;
  }

  StoryboardState.sourceImages[ideaId] = imageUrl;
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }
}

/**
 * Check if source image has changed (for re-split detection)
 * @param {string} ideaId
 * @param {string} currentImageUrl
 * @returns {boolean}
 */
export function hasSourceImageChanged(ideaId, currentImageUrl) {
  const tracked = StoryboardState.sourceImages[ideaId];
  if (!tracked) return true; // No tracked image = new
  return tracked !== currentImageUrl;
}

// ============================================================================
// STORYBOARD OPERATIONS
// ============================================================================

/**
 * Update storyboard style
 * @param {string} styleText - New style text
 */
export function updateStoryboardStyle(styleText) {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return;

  const storyboard = getStoryboard(ideaId);
  if (!storyboard) return;

  // Update style
  if (!storyboard.style) {
    storyboard.style = {};
  }
  storyboard.style.visual = styleText;

  // Persist changes
  saveToSession();
  StoryboardState.isDirty = true;

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Updated style:', styleText);
}

// ============================================================================
// SCENE OPERATIONS
// ============================================================================

/**
 * Update a scene field (visual, camera, audio, voiceover, setting, subject, action, mood)
 * @param {number} sceneSeq - Scene sequence number
 * @param {string} field - Field name to update
 * @param {string} value - New value
 */
export function updateSceneField(sceneSeq, field, value) {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return;

  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  // Mark as edited if first change
  if (!scene.isEdited) {
    scene.isEdited = true;
  }

  // Handle different field types
  switch (field) {
    case 'visual':
      scene.visual = value;
      scene.visualPrompt = value;
      break;
    case 'camera':
      // Store as string (user edited)
      scene.camera = value;
      break;
    case 'audio':
      // Store as string (music/SFX - user edited)
      scene.audio = value;
      break;
    case 'voiceover':
      // Store as string (spoken narration - user edited)
      scene.voiceover = value;
      break;
    case 'setting':
    case 'subject':
    case 'action':
    case 'mood':
      scene[field] = value;
      break;
    default:
      console.warn('[Storyboard] Unknown field:', field);
      return;
  }

  // Persist changes
  saveToSession();
  StoryboardState.isDirty = true;

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Updated scene', sceneSeq, 'field:', field);
}

/**
 * Update a scene's frame image URL
 * @param {number} sceneSeq
 * @param {'start'|'end'} frameType
 * @param {string} imageUrl
 * @param {'generated'|'pool'|'upload'} source - Where the image came from
 */
export function setSceneFrame(sceneSeq, frameType, imageUrl, source = 'generated') {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return;

  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  // Initialize frames object if needed
  if (!scene.frames) {
    scene.frames = { start: null, end: null };
  }

  // Store frame data with URL, metadata, and source
  scene.frames[frameType] = {
    url: imageUrl,
    generatedAt: new Date().toISOString(),
    source: source
  };

  // Save changes
  saveToSession();
  setFrameStatus(sceneSeq, frameType, 'complete');
  StoryboardState.isDirty = true;

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId).catch(err => console.error('[Storyboard] Frame auto-save failed:', err));
  }

  console.log('[Storyboard] Set', frameType, 'frame for scene', sceneSeq, '(source:', source + ')');
}

// ============================================================================
// SCENE VIDEO MANAGEMENT
// ============================================================================

/**
 * Set video URL for a scene - ADDS to history, doesn't replace
 * Task 113 Issue #6: Preserve all video generations in history
 * @param {number} sceneSeq - Scene sequence number
 * @param {string} videoUrl - Video URL
 * @param {string} provider - Provider used (veo, bytedance)
 * @param {Object} meta - Additional metadata (prompt, modelId, etc.)
 */
export function setSceneVideo(sceneSeq, videoUrl, provider = 'veo', meta = {}) {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return;

  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  // Initialize videos array if needed (Task 113: Video History)
  if (!scene.videos) {
    scene.videos = [];
    // Migrate existing scene.video if present
    if (scene.video?.url) {
      scene.videos.push({
        ...scene.video,
        id: `vid_${Date.now()}_legacy`,
        isCurrent: false
      });
    }
  }

  // Mark all existing videos as not current
  scene.videos.forEach(v => v.isCurrent = false);

  // Create new video object with unique ID
  const newVideo = {
    id: `vid_${Date.now()}`,
    url: videoUrl,
    status: 'complete',
    provider: provider,
    generatedAt: new Date().toISOString(),
    isCurrent: true,
    ...meta
  };

  // Add to history array
  scene.videos.push(newVideo);

  // Keep scene.video for backwards compatibility (points to current)
  scene.video = newVideo;

  StoryboardState.isDirty = true;
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  // Sync with GeneratorState if idea is there
  syncVideoToGeneratorState(ideaId, sceneSeq, videoUrl, provider);

  console.log('[Storyboard] Added video', newVideo.id, 'to scene', sceneSeq, '- total:', scene.videos.length);
}

/**
 * Get all scene videos for current storyboard
 * Task 113 Issue #6: Returns ALL videos from ALL scenes (not just current)
 * @returns {Array} Array of { sceneSeq, timing, visual, video, isCurrent } objects
 */
export function getSceneVideos() {
  const storyboard = StoryboardState.byIdeaId[StoryboardState.activeIdeaId];
  if (!storyboard?.scenes) return [];

  // Helper to extract filename for deduplication
  const getFilename = (url) => {
    if (!url) return '';
    try {
      return new URL(url).pathname.split('/').pop() || url;
    } catch {
      return url;
    }
  };

  // Return ALL videos from ALL scenes, sorted by generatedAt (newest first)
  const allVideos = [];
  const seenFilenames = new Set();  // Dedupe by filename

  storyboard.scenes.forEach((scene) => {
    // Support both new videos[] array and legacy scene.video
    const videos = scene.videos || (scene.video?.url ? [scene.video] : []);
    videos.forEach(video => {
      if (video?.url) {
        const filename = getFilename(video.url);
        // Skip if we've already seen this video filename
        if (seenFilenames.has(filename)) return;
        seenFilenames.add(filename);

        allVideos.push({
          sceneSeq: scene.seq,
          timing: scene.timing,
          visual: scene.visual,
          video: video,
          isCurrent: video.isCurrent !== false // backwards compat: if no isCurrent field, assume true
        });
      }
    });
  });

  // Sort by generatedAt, newest first
  return allVideos.sort((a, b) =>
    new Date(b.video.generatedAt || 0) - new Date(a.video.generatedAt || 0)
  );
}

/**
 * Get video history for a specific scene
 * Task 113 Issue #6
 * @param {number} sceneSeq - Scene sequence number
 * @returns {Array} Array of video objects for this scene
 */
export function getSceneVideoHistory(sceneSeq) {
  const storyboard = StoryboardState.byIdeaId[StoryboardState.activeIdeaId];
  const scene = storyboard?.scenes?.find(s => s.seq === sceneSeq);
  if (!scene) return [];

  // Support both new videos[] array and legacy scene.video
  return scene.videos || (scene.video?.url ? [scene.video] : []);
}

/**
 * Set a specific video as the current one for a scene
 * Task 113 Issue #6: Allow user to switch between video versions
 * @param {number} sceneSeq - Scene sequence number
 * @param {string} videoId - The video ID to set as current
 */
export function setCurrentVideo(sceneSeq, videoId) {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return;

  const storyboard = getStoryboard(ideaId);
  const scene = storyboard?.scenes?.find(s => s.seq === sceneSeq);
  if (!scene?.videos) return;

  // Mark the selected video as current, others as not current
  scene.videos.forEach(v => v.isCurrent = (v.id === videoId));

  // Log for debugging
  const found = scene.videos.find(v => v.id === videoId);
  if (!found) {
    console.warn('[Storyboard] setCurrentVideo: video not found by ID:', videoId,
      'Available IDs:', scene.videos.map(v => v.id));
  }

  // Update scene.video for backwards compatibility
  scene.video = scene.videos.find(v => v.isCurrent) || scene.videos[scene.videos.length - 1];

  StoryboardState.isDirty = true;
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  // Sync with GeneratorState
  if (scene.video?.url) {
    syncVideoToGeneratorState(ideaId, sceneSeq, scene.video.url, scene.video.provider);
  }

  console.log('[Storyboard] Set current video for scene', sceneSeq, 'to', videoId);
}

/**
 * Delete a specific video from scene history
 * Task 113 Issue #6
 * @param {number} sceneSeq - Scene sequence number
 * @param {string} videoId - The video ID to delete
 */
export function deleteVideoFromHistory(sceneSeq, videoId) {
  const ideaId = StoryboardState.activeIdeaId;
  if (!ideaId) return;

  const storyboard = getStoryboard(ideaId);
  const scene = storyboard?.scenes?.find(s => s.seq === sceneSeq);
  if (!scene?.videos) return;

  const index = scene.videos.findIndex(v => v.id === videoId);
  if (index === -1) return;

  const wasCurrentVideo = scene.videos[index].isCurrent;
  scene.videos.splice(index, 1);

  // If we deleted the current video, make the most recent one current
  if (wasCurrentVideo && scene.videos.length > 0) {
    scene.videos[scene.videos.length - 1].isCurrent = true;
    scene.video = scene.videos[scene.videos.length - 1];
  } else if (scene.videos.length === 0) {
    scene.video = { url: null, status: 'pending', provider: null };
  }

  StoryboardState.isDirty = true;
  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Deleted video', videoId, 'from scene', sceneSeq, '- remaining:', scene.videos.length);
}

/**
 * Update scene video generation status
 * @param {number} sceneSeq - Scene sequence
 * @param {string} status - 'pending' | 'generating' | 'complete' | 'error'
 */
export function setSceneVideoStatus(sceneSeq, status) {
  const storyboard = StoryboardState.byIdeaId[StoryboardState.activeIdeaId];
  if (!storyboard) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  if (!scene.video) {
    scene.video = { url: null, status: status, provider: null };
  } else {
    scene.video.status = status;
  }

  saveToSession();
}

/**
 * Delete video from a scene
 * @param {number} sceneSeq - Scene sequence number
 */
export function deleteSceneVideo(sceneSeq) {
  const storyboard = StoryboardState.byIdeaId[StoryboardState.activeIdeaId];
  if (!storyboard) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  scene.video = { url: null, status: 'pending', provider: null };
  StoryboardState.isDirty = true;
  saveToSession();

  // Auto-save to database if this is a saved idea
  const ideaId = StoryboardState.activeIdeaId;
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Deleted video from scene', sceneSeq);
}

/**
 * Sync video URL to GeneratorState for persistence
 */
function syncVideoToGeneratorState(ideaId, sceneSeq, videoUrl, provider) {
  if (typeof window !== 'undefined' && GeneratorState) {
    const idea = GeneratorState.generatedIdeas?.find(i =>
      i.id === ideaId || i._dbId === ideaId
    );
    if (idea?.meta?.storyboard?.scenes) {
      const scene = idea.meta.storyboard.scenes.find(s => s.seq === sceneSeq);
      if (scene) {
        scene.video = { url: videoUrl, status: 'complete', provider: provider };
      }
    }
  }
}

/**
 * Update a scene's visual prompt
 * @param {string} ideaId
 * @param {number} sceneSeq
 * @param {string} newPrompt
 */
export function updateScenePrompt(ideaId, sceneSeq, newPrompt) {
  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene) return;

  // Store original if first edit
  if (!scene.isEdited) {
    scene.originalPrompt = scene.visualPrompt || scene.visual;
    scene.isEdited = true;
  }

  scene.visualPrompt = newPrompt;
  scene.visual = newPrompt; // Keep both for compatibility

  saveToSession();
  StoryboardState.isDirty = true;

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }
}

/**
 * Reset a scene to original prompt
 * @param {string} ideaId
 * @param {number} sceneSeq
 */
export function resetScenePrompt(ideaId, sceneSeq) {
  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return;

  const scene = storyboard.scenes.find(s => s.seq === sceneSeq);
  if (!scene || !scene.isEdited) return;

  scene.visualPrompt = scene.originalPrompt;
  scene.visual = scene.originalPrompt;
  scene.isEdited = false;

  saveToSession();

  // Auto-save to database if this is a saved idea
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }
}

// ============================================================================
// SCENE CRUD OPERATIONS
// ============================================================================

/**
 * Helper: Re-calculate sequence numbers after scene add/remove
 * @param {Object} storyboard
 */
function resequenceScenes(storyboard) {
  storyboard.scenes.forEach((scene, index) => {
    scene.seq = index + 1;
    scene.id = `scene_${scene.seq}`;
    // Note: timing could be recalculated here based on duration/scene count
  });
}

/**
 * Duplicate a scene
 * Creates a copy of the scene at sceneSeq and inserts it after
 * @param {string} ideaId
 * @param {number} sceneSeq - Sequence number to duplicate
 * @returns {Object|null} The new scene or null on failure
 */
export function duplicateScene(ideaId, sceneSeq) {
  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return null;

  const sourceIndex = storyboard.scenes.findIndex(s => s.seq === sceneSeq);
  if (sourceIndex === -1) return null;

  // Deep clone the scene
  const newScene = JSON.parse(JSON.stringify(storyboard.scenes[sourceIndex]));

  // Generate new unique ID
  newScene.id = `scene_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
  newScene.isEdited = true;

  // Clear frames so user can re-assign for the copy (design choice)
  newScene.frames = { start: null, end: null };

  // Insert after source
  storyboard.scenes.splice(sourceIndex + 1, 0, newScene);

  // Re-sequence all scenes
  resequenceScenes(storyboard);

  // Save changes
  setStoryboard(ideaId, storyboard);

  // Save to database if applicable
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Duplicated scene', sceneSeq, '→ new scene at position', sourceIndex + 2);
  return newScene;
}

/**
 * Delete a scene
 * @param {string} ideaId
 * @param {number} sceneSeq - Sequence number to delete
 * @returns {boolean} True if deleted, false if not allowed
 */
export function deleteScene(ideaId, sceneSeq) {
  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return false;

  // Prevent deleting last scene
  if (storyboard.scenes.length <= 1) {
    console.warn('[Storyboard] Cannot delete last scene');
    return false;
  }

  const deleteIndex = storyboard.scenes.findIndex(s => s.seq === sceneSeq);
  if (deleteIndex === -1) return false;

  // Remove the scene
  storyboard.scenes.splice(deleteIndex, 1);

  // Re-sequence remaining scenes
  resequenceScenes(storyboard);

  // Save changes
  setStoryboard(ideaId, storyboard);

  // Save to database if applicable
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Deleted scene', sceneSeq);
  return true;
}

/**
 * Insert a blank scene after a given position
 * @param {string} ideaId
 * @param {number} afterSeq - Insert after this scene number (0 = insert at beginning)
 * @returns {Object|null} The new scene or null on failure
 */
export function insertScene(ideaId, afterSeq) {
  const storyboard = getStoryboard(ideaId);
  if (!storyboard?.scenes) return null;

  const insertIndex = afterSeq; // 0-based index to insert AT

  const newScene = {
    id: `scene_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
    seq: afterSeq + 1,
    timing: '0s-10s',
    visual: '',
    visualPrompt: '',
    setting: '',
    subject: '',
    action: '',
    mood: '',
    camera: '',
    audio: '',
    frames: { start: null, end: null },
    video: { url: null, status: 'pending', provider: null },
    isEdited: true,
    originalPrompt: null
  };

  storyboard.scenes.splice(insertIndex, 0, newScene);

  // Re-sequence all scenes
  resequenceScenes(storyboard);

  // Save changes
  setStoryboard(ideaId, storyboard);

  // Save to database if applicable
  if (isDatabaseIdea(ideaId)) {
    saveToDatabase(ideaId);
  }

  console.log('[Storyboard] Inserted blank scene after position', afterSeq);
  return newScene;
}

// ============================================================================
// SESSION STORAGE PERSISTENCE
// ============================================================================

/**
 * Load storyboards from sessionStorage
 * @returns {Object} Object with byIdeaId, panelPools, splitSettings, sourceImages
 */
function loadFromSession() {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return { byIdeaId: {}, panelPools: {}, splitSettings: {}, sourceImages: {} };

    // NOTE: We removed the 4MB size check that was wiping data.
    // With R2 storage, panels are now stored as URLs (~100 bytes each) not Base64 (~1.5MB each).
    // The 5MB sessionStorage limit is no longer a concern.

    const parsed = JSON.parse(stored);

    // Handle legacy format (just byIdeaId object)
    if (!parsed.byIdeaId) {
      return { byIdeaId: parsed, panelPools: {}, splitSettings: {}, sourceImages: {} };
    }

    return {
      byIdeaId: parsed.byIdeaId || {},
      panelPools: parsed.panelPools || {},
      splitSettings: parsed.splitSettings || {},
      sourceImages: parsed.sourceImages || {}
    };
  } catch (e) {
    // Only clear if JSON is actually corrupted (SyntaxError)
    if (e instanceof SyntaxError) {
      console.error('[Storyboard] Session data corrupted (invalid JSON), clearing:', e.message);
      sessionStorage.removeItem(STORAGE_KEY);
    } else {
      console.warn('[Storyboard] Failed to load from session:', e);
    }
    return { byIdeaId: {}, panelPools: {}, splitSettings: {}, sourceImages: {} };
  }
}

/**
 * Save current storyboards and panel pools to sessionStorage
 * Handles QuotaExceededError gracefully (shouldn't happen with R2 URLs)
 */
function saveToSession() {
  try {
    const data = {
      byIdeaId: StoryboardState.byIdeaId,
      panelPools: StoryboardState.panelPools,
      splitSettings: StoryboardState.splitSettings,
      sourceImages: StoryboardState.sourceImages
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    if (e.name === 'QuotaExceededError' || e.name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      // This shouldn't happen with URL-based storage, but handle gracefully
      console.error('[Storyboard] Storage quota exceeded! Data may contain Base64 images.');
      console.error('[Storyboard] Panel count:', Object.values(StoryboardState.panelPools).flat().length);
      // Don't alert every time, just log for debugging
    } else {
      console.warn('[Storyboard] Failed to save to session:', e);
    }
  }
}

/**
 * Clear all storyboards from session
 */
export function clearSession() {
  StoryboardState.byIdeaId = {};
  StoryboardState.panelPools = {};
  StoryboardState.splitSettings = {};
  StoryboardState.sourceImages = {};
  StoryboardState.generationStatus = {};
  StoryboardState.frameGenerationStatus = {};
  sessionStorage.removeItem(STORAGE_KEY);
}

// ============================================================================
// DATABASE PERSISTENCE
// ============================================================================

/**
 * Check if an idea is a database idea (UUID format)
 * BUG 3 FIX: Added UUID regex validation for more reliable detection
 * Uses shared uuid-utils.js for consistent validation across codebase
 * @param {string} ideaId
 * @returns {boolean}
 */
export function isDatabaseIdea(ideaId) {
  if (!ideaId) return false;

  // Accept valid UUIDs directly (uses shared utility)
  if (isValidUUID(ideaId)) return true;

  // Fallback: length check for backwards compatibility
  // Temporary generator ideas are like "idea_1", "idea_2", etc.
  return ideaId.length > 20 && !ideaId.startsWith('idea_');
}

/**
 * Execute a promise with timeout
 * @param {Promise} promise - The promise to wrap
 * @param {number} timeoutMs - Timeout in milliseconds
 * @returns {Promise} - Rejects with TimeoutError if timeout expires
 */
function withTimeout(promise, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('TimeoutError'));
    }, timeoutMs);

    promise
      .then(result => {
        clearTimeout(timer);
        resolve(result);
      })
      .catch(err => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

/**
 * Execute with retry and exponential backoff
 * @param {Function} fn - Async function to execute
 * @param {Object} options - Retry options
 * @returns {Promise} - Resolves with result or rejects after all retries exhausted
 */
async function withRetry(fn, options = {}) {
  const maxAttempts = options.maxAttempts || RETRY.maxAttempts;
  const backoffMs = options.backoffMs || RETRY.backoffMs;
  const backoffMultiplier = options.backoffMultiplier || RETRY.backoffMultiplier;
  const timeoutMs = options.timeoutMs || TIMEOUTS.databaseSave;

  let lastError;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await withTimeout(fn(), timeoutMs);
    } catch (err) {
      lastError = err;
      const isTimeout = err.message === 'TimeoutError';
      console.warn(`[Storyboard] Save attempt ${attempt}/${maxAttempts} failed:`,
        isTimeout ? 'timeout' : err.message);

      if (attempt < maxAttempts) {
        // Exponential backoff
        const delay = backoffMs * Math.pow(backoffMultiplier, attempt - 1);
        console.log(`[Storyboard] Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
}

/**
 * Show toast notification for save failure
 * Only shown after all retries are exhausted
 */
function notifySaveFailure() {
  // Try to get Toast from window (may be loaded by Dashboard or Generator)
  const Toast = window.DashboardCore?.Toast || window.Toast;
  if (Toast?.error) {
    Toast.error('Changes not saved. Please try again.');
  } else {
    console.error('[Storyboard] No toast available for notification');
  }
}

/**
 * Save storyboard AND panel pool to database (for saved ideas only)
 * Persists: storyboard, panelPool, splitSettings, panelSourceImage
 * Now includes timeout (5s) and retry (3 attempts) for reliability
 * BUG 3 FIX: Improved ID resolution with UUID regex validation
 * @param {string} ideaId - Can be generator ID (A1-xxx) or database UUID
 * @returns {Promise<boolean>}
 */
export async function saveToDatabase(ideaId) {
  // Try to resolve generator ID to database UUID
  let dbId = ideaId;

  // FIRST: Check if it's already a valid UUID (uses shared utility)
  if (isValidUUID(ideaId)) {
    dbId = ideaId;  // Already a UUID, use directly
    console.log(`[Storyboard] Using existing UUID: ${dbId}`);
  }
  // THEN: Check mapping for generator IDs (even if shorter than 20 chars)
  else if (GeneratorState?.idMapping?.[ideaId]) {
    dbId = GeneratorState.idMapping[ideaId];
    console.log(`[Storyboard] Resolved ${ideaId} → ${dbId}`);
  }

  // NOW check if resolved ID is valid for database
  if (!isDatabaseIdea(dbId)) {
    console.log('[Storyboard] Skipping DB save - not a database idea:', dbId);
    return false;
  }

  // Use the resolved database ID for all operations
  ideaId = dbId;

  const storyboard = getStoryboard(ideaId);
  const panelPool = getPanelPool(ideaId);
  const splitSettings = getSplitSettings(ideaId);
  const sourceImage = getSourceImage(ideaId);

  // Build meta object
  // IMPORTANT: Always include panelPool (even if empty) to allow clearing
  const meta = {};
  if (storyboard) meta.storyboard = storyboard;

  // Always set panelPool - use null to clear from database when empty
  meta.panelPool = panelPool.length > 0 ? panelPool : null;

  if (splitSettings && (splitSettings.rows !== 3 || splitSettings.cols !== 2)) {
    meta.splitSettings = splitSettings;
  }
  if (sourceImage) meta.panelSourceImage = sourceImage;

  if (Object.keys(meta).length === 0) {
    console.log('[Storyboard] Nothing to save');
    return false;
  }

  try {
    // Use retry wrapper with timeout for reliability
    await withRetry(async () => {
      const { api } = await import('../../../js/api/client.js');
      await api.updateIdea(ideaId, { meta });
    });

    StoryboardState.isDirty = false;
    console.log('[Storyboard] Saved to database:',
      storyboard ? `${storyboard.scenes?.length || 0} scenes` : 'no storyboard',
      panelPool.length > 0 ? `, ${panelPool.length} panels` : ''
    );
    // Update local timestamp for sorting in Dashboard Recent Ideas
    window.DashboardCore?.Utils?.touchIdea(ideaId);
    return true;
  } catch (error) {
    console.error('[Storyboard] Failed to save to database after retries:', error);
    // Notify user of failure (only after all retries exhausted)
    notifySaveFailure();
    return false;
  }
}

/**
 * Load storyboard from database idea
 * @param {Object} idea - Idea object with meta
 * @returns {Object|null}
 */
export function loadFromIdea(idea) {
  if (!idea?.id) return null;

  // ========================================
  // CHECK IF ALREADY LOADED IN MEMORY
  // ========================================
  // If we already have a storyboard for this idea in memory, DON'T overwrite it!
  // This preserves videos/panels generated in the current session.
  const existing = StoryboardState.byIdeaId[idea.id];
  if (existing?.scenes?.length > 0) {
    console.log('[Storyboard] Already in memory, skipping load from idea:', existing.scenes.length, 'scenes');
    return existing;
  }

  // ========================================
  // LOAD PANEL POOL FROM DATABASE (Phase 4)
  // ========================================
  // Load panels FIRST - they persist regardless of storyboard source
  if (idea.meta?.panelPool?.length > 0) {
    console.log('[Storyboard] Loading panel pool from database:', idea.meta.panelPool.length, 'panels');
    setPanelPool(idea.id, idea.meta.panelPool);
  }

  // Load split settings
  if (idea.meta?.splitSettings) {
    const { rows, cols } = idea.meta.splitSettings;
    console.log('[Storyboard] Loading split settings from database:', rows, 'x', cols);
    setSplitSettings(idea.id, rows, cols);
  }

  // Load source image URL
  if (idea.meta?.panelSourceImage) {
    console.log('[Storyboard] Loading source image from database');
    setSourceImage(idea.id, idea.meta.panelSourceImage);
  }

  // PRIORITY 1: Check for EDITED storyboard (user has made and saved changes)
  // This takes precedence because user edits should NOT be overwritten by original data
  const storyboard = idea.meta?.storyboard;
  if (storyboard && storyboard.scenes?.length > 0) {
    console.log('[Storyboard] Loading EDITED storyboard:', storyboard.scenes.length, 'scenes');
    const normalized = normalizeStoryboard(storyboard);
    setStoryboard(idea.id, normalized);
    return normalized;
  }

  // PRIORITY 2: Check for ORIGINAL video scenes (from video idea generation)
  // These are the initial source - have correct timing, audio, camera from the AI
  const videoScenes = idea.meta?.structure?.scenes || idea.meta?.scenes;
  if (videoScenes && Array.isArray(videoScenes) && videoScenes.length > 0) {
    console.log('[Storyboard] Loading ORIGINAL video scenes:', videoScenes.length);
    console.log('[Storyboard] Scene 1 audio:', videoScenes[0]?.audio);

    const normalized = {
      version: 1,
      generatedAt: new Date().toISOString(),
      loadedFromIdea: true, // Flag to indicate these are original video scenes
      style: {
        visual: idea.meta?.videoStyle || idea.meta?.structure?.style || 'Cinematic commercial',
        colorGrade: 'Natural',
        aspectRatio: '16:9'
      },
      duration: idea.meta?.videoDuration || idea.meta?.structure?.duration || ':30',
      scenes: videoScenes.map((scene, idx) => {
        const normalized = normalizeScene(scene);
        console.log(`[Storyboard] Normalized scene ${idx + 1} audio:`, normalized.audio);
        return normalized;
      }),
      endFrame: {
        description: idea.meta?.structure?.super || idea.headline || '',
        duration: '3s',
        image: null
      }
    };
    setStoryboard(idea.id, normalized);
    return normalized;
  }

  // PRIORITY 3: Check for legacy generatedScenes format
  const legacyScenes = idea.meta?.generatedScenes;
  if (legacyScenes) {
    console.log('[Storyboard] Loading legacy generatedScenes');
    const normalized = normalizeStoryboard(legacyScenes);
    setStoryboard(idea.id, normalized);
    return normalized;
  }

  return null;
}

/**
 * Load videos from database and merge into storyboard scenes
 * This ensures all generated videos are available even if session storage is stale
 * @param {string} ideaId - The idea ID
 * @returns {Promise<void>}
 */
export async function loadVideosFromDatabase(ideaId) {
  if (!ideaId) return;

  const storyboard = StoryboardState.byIdeaId[ideaId];
  if (!storyboard?.scenes) {
    console.log('[Storyboard] No storyboard to load videos into');
    return;
  }

  try {
    const { api } = await import('../../../js/api/client.js');
    const result = await api.getIdeaVideos(ideaId);

    if (!result.success || !result.videos?.length) {
      console.log('[Storyboard] No videos in database for idea:', ideaId);
      return;
    }

    console.log('[Storyboard] Loading', result.videos.length, 'videos from database for idea:', ideaId);

    // Group videos by sceneSeq from meta
    const videosByScene = {};
    for (const dbVideo of result.videos) {
      const sceneSeq = dbVideo.meta?.sceneSeq;
      if (sceneSeq !== undefined) {
        if (!videosByScene[sceneSeq]) {
          videosByScene[sceneSeq] = [];
        }
        videosByScene[sceneSeq].push({
          id: dbVideo.id,
          url: dbVideo.url,
          status: 'complete',
          provider: dbVideo.provider,
          generatedAt: dbVideo.created_at,
          isCurrent: false  // Will be set later
        });
      }
    }

    // Helper to normalize URL for comparison (extract filename to handle fal.media vs r2 differences)
    const normalizeVideoUrl = (url) => {
      if (!url) return '';
      try {
        // Extract just the filename from the URL path (e.g., "abc123.mp4")
        const pathname = new URL(url).pathname;
        const filename = pathname.split('/').pop();
        return filename || url;
      } catch {
        return url;
      }
    };

    // For saved ideas, database is source of truth - replace session videos with database videos
    // This prevents stale session data (with fal.media URLs) from duplicating database videos (with R2 URLs)
    let addedCount = 0;
    for (const scene of storyboard.scenes) {
      const dbVideos = videosByScene[scene.seq];
      if (!dbVideos?.length) continue;

      // Replace session videos with database videos for this scene
      // Database videos have R2 URLs and are the canonical source
      scene.videos = [...dbVideos];
      addedCount += dbVideos.length;

      // Sort by generatedAt (newest first) and mark newest as current
      scene.videos.sort((a, b) =>
        new Date(b.generatedAt || 0) - new Date(a.generatedAt || 0)
      );

      // Mark the newest as current
      scene.videos.forEach((v, i) => v.isCurrent = (i === 0));

      // Update scene.video for backwards compatibility
      scene.video = scene.videos[0];
    }

    // Also clear videos from scenes that have no database videos
    // (they might have stale session-only videos)
    for (const scene of storyboard.scenes) {
      if (!videosByScene[scene.seq]) {
        scene.videos = [];
        scene.video = null;
      }
    }

    if (addedCount > 0) {
      console.log('[Storyboard] Replaced with', addedCount, 'videos from database');
      saveToSession();
    }
  } catch (err) {
    console.error('[Storyboard] Error loading videos from database:', err);
  }
}

/**
 * Normalize storyboard data from old/new formats
 * @param {Object} data
 * @returns {Object}
 */
function normalizeStoryboard(data) {
  // Already new format - but still normalize scenes for audio/camera consistency
  if (data.version && data.scenes) {
    return {
      ...data,
      scenes: data.scenes.map(normalizeScene)  // Always normalize scenes!
    };
  }

  // Old format (generatedScenes)
  if (data.scenes && Array.isArray(data.scenes)) {
    return {
      version: 1,
      generatedAt: new Date().toISOString(),
      style: {
        visual: data.style || 'Cinematic commercial',
        colorGrade: 'Natural',
        aspectRatio: '16:9'
      },
      duration: data.duration || ':30',
      scenes: data.scenes.map(normalizeScene),
      endFrame: {
        description: data.endFrame || '',
        duration: '3s',
        image: null
      }
    };
  }

  return data;
}

/**
 * Extract voiceover from old merged audio strings
 * For backward compatibility with data that has "VO: 'text' | SFX: ..." format
 * @param {string} audioStr - The audio string to parse
 * @returns {string|null} Extracted voiceover text or null
 */
function extractVOFromAudio(audioStr) {
  if (!audioStr || typeof audioStr !== 'string') return null;

  // Pattern 1: VO: "text" or VO: 'text'
  const quotedMatch = audioStr.match(/VO:\s*["']([^"']+)["']/i);
  if (quotedMatch) return quotedMatch[1].trim();

  // Pattern 2: VO: text (without quotes, before pipe or end)
  const unquotedMatch = audioStr.match(/VO:\s*([^|]+)/i);
  if (unquotedMatch) {
    const text = unquotedMatch[1].trim();
    // Don't return if it looks like SFX/Music was captured
    if (!text.match(/^(SFX|Music|Sound):/i)) {
      return text;
    }
  }

  return null;
}

/**
 * Normalize audio data - keep as string for display
 * Strips prefixes like "SFX:", "VO:", "Music:" that AI adds
 * Also removes voiceover from audio (should be in separate field now)
 * @param {string|Object} audio
 * @returns {string} Audio as displayable string (clean, no prefixes, no VO)
 */
function normalizeAudio(audio) {
  // Already empty
  if (!audio) {
    return '';
  }

  // String - clean up and remove VO sections (VO now belongs in voiceover field)
  if (typeof audio === 'string') {
    let cleaned = audio.trim();

    // Remove nested VO: "SFX: ..." patterns from corrupted data
    if (cleaned.startsWith('VO: "SFX:') || cleaned.startsWith('VO: "Music:')) {
      const match = cleaned.match(/^VO: "(?:SFX:|Music:)\s*(.+)"$/);
      if (match) {
        cleaned = match[1];
      }
    }

    // Remove VO sections from merged strings (e.g., "VO: 'Hello' | SFX: bang | Music: tense")
    // VO should be in the separate voiceover field now
    cleaned = cleaned.replace(/VO:\s*["']?[^|"']*["']?\s*\|?\s*/gi, '');

    // Remove standalone VO: " prefix with quotes (if entire string is just VO)
    if (cleaned.startsWith('VO: "') && cleaned.endsWith('"')) {
      cleaned = ''; // It was only voiceover, audio should be empty
    }

    // Remove common prefixes (with or without space after colon)
    // Handles: "SFX: content", "SFX:content", "Music: content"
    const prefixPatterns = [
      /^SFX:\s*/i,
      /^Music:\s*/i,
      /^Sound:\s*/i,
      /^Audio:\s*/i
    ];

    for (const pattern of prefixPatterns) {
      if (pattern.test(cleaned)) {
        cleaned = cleaned.replace(pattern, '');
        break; // Only remove first matching prefix
      }
    }

    // Clean up any remaining pipe separators at start/end
    cleaned = cleaned.replace(/^\s*\|\s*/, '').replace(/\s*\|\s*$/, '');

    // Remove surrounding quotes if present
    if ((cleaned.startsWith('"') && cleaned.endsWith('"')) ||
        (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
      cleaned = cleaned.slice(1, -1);
    }

    return cleaned.trim();
  }

  // Object format - extract the actual content (without adding prefixes)
  if (typeof audio === 'object') {
    // Just return the content, not wrapped in prefixes
    return audio.voiceover || audio.sfx || audio.music || '';
  }

  return '';
}

/**
 * Normalize a single scene from old format
 * @param {Object} scene
 * @returns {Object}
 */
function normalizeScene(scene) {
  return {
    id: scene.id || `scene_${scene.seq}`,
    seq: scene.seq,
    timing: scene.timing,

    // Visual prompt
    visualPrompt: scene.visualPrompt || scene.visual,
    visual: scene.visual,

    // Structured metadata (parse from visual if not present)
    setting: scene.setting || '',
    subject: scene.subject || '',
    action: scene.action || '',
    mood: scene.mood || '',

    // Camera
    camera: typeof scene.camera === 'object' ? scene.camera : {
      shot: '',
      movement: scene.camera || '',
      angle: ''
    },

    // Audio (music/SFX) - keep as string if it's already formatted, or parse if structured
    // Note: normalizeAudio now strips VO sections from merged strings
    audio: normalizeAudio(scene.audio),

    // Voiceover (spoken narration) - separate from audio/music
    // For backward compatibility: extract from old merged audio if voiceover field is empty
    // Filter out literal "null" strings from AI responses
    voiceover: (scene.voiceover && scene.voiceover.toLowerCase() !== 'null')
      ? scene.voiceover
      : (extractVOFromAudio(scene.audio) || ''),

    // Frames (start/end images)
    frames: scene.frames || {
      start: null,
      end: null
    },

    // Task 113 Issue #6: Video History Migration
    // Migrate legacy scene.video to scene.videos[] array
    videos: migrateVideoToVideosArray(scene),

    // Video - backwards compatibility getter (points to current video)
    video: (() => {
      const videos = scene.videos || (scene.video?.url ? [scene.video] : []);
      const current = videos.find(v => v.isCurrent) || videos[videos.length - 1];
      return current || { url: null, status: 'pending', provider: null };
    })(),

    // Edit state
    isEdited: scene.isEdited || false,
    originalPrompt: scene.originalPrompt || null
  };
}

/**
 * Migrate legacy scene.video to scene.videos[] array
 * Task 113 Issue #6: Video History
 */
function migrateVideoToVideosArray(scene) {
  // Already has videos array - use it
  if (scene.videos && Array.isArray(scene.videos)) {
    return scene.videos.map(v => ({
      ...v,
      id: v.id || `vid_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      isCurrent: v.isCurrent !== undefined ? v.isCurrent : true
    }));
  }

  // Has legacy scene.video with URL - migrate it
  if (scene.video?.url) {
    return [{
      ...scene.video,
      id: scene.video.id || `vid_${Date.now()}_migrated`,
      isCurrent: true
    }];
  }

  // No videos
  return [];
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize storyboard state from sessionStorage
 * Normalizes all storyboards on load to fix any legacy data with prefixes
 */
export function initStoryboardState() {
  const stored = loadFromSession();

  // Normalize all storyboards on init (fixes old data with SFX:/VO: prefixes and video arrays)
  let needsSave = false;
  for (const [ideaId, storyboard] of Object.entries(stored.byIdeaId)) {
    const normalized = normalizeStoryboard(storyboard);
    StoryboardState.byIdeaId[ideaId] = normalized;

    // Check if normalization changed anything (e.g., migrated video arrays)
    const origVideos = storyboard.scenes?.reduce((sum, s) => sum + (s.videos?.length || 0), 0) || 0;
    const normVideos = normalized.scenes?.reduce((sum, s) => sum + (s.videos?.length || 0), 0) || 0;
    if (origVideos !== normVideos) {
      needsSave = true;
      console.log('[Storyboard] Migrated videos for', ideaId, ':', origVideos, '->', normVideos);
    }
  }

  Object.assign(StoryboardState.panelPools, stored.panelPools);
  Object.assign(StoryboardState.splitSettings, stored.splitSettings || {});
  Object.assign(StoryboardState.sourceImages, stored.sourceImages || {});

  // Persist normalized data if anything changed
  if (needsSave) {
    saveToSession();
    console.log('[Storyboard] Saved normalized storyboards to session');
  }

  console.log('[Storyboard] Initialized with', Object.keys(stored.byIdeaId).length, 'storyboards,', Object.keys(stored.panelPools).length, 'panel pools');
}

// Initialize on module load
initStoryboardState();

// ============================================================================
// GLOBAL EXPORTS
// ============================================================================

window.StoryboardState = StoryboardState;

export { StoryboardState };
